import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
    McButton,
    McCard,
    McIcon,
    McInput,
    McSelect,
    McTextarea,
    McTag,
    McLoadingIndicator
} from '@maersk-global/mds-react-wrapper';
import { sanitizeInput } from '../../../utils/validators';
import { getCategoryOptions } from '../../../utils/categoryConfig';

interface InfoStepProps {
    formData: {
        queryId: string;
        name: string;
        description: string;
        category: string;
        applicationId: string;
        notes: string;
        makeCurrent: boolean;
    };
    applications: any[];
    tags: string[];
    currentTag: string;
    onInputChange: (field: string, value: any) => void;
    onAddTag: (tag?: string | React.MouseEvent) => void;
    onRemoveTag: (tag: string) => void;
    onCurrentTagChange: (value: string) => void;
    onNext: () => void;
    onBack?: () => void;
    isSaving?: boolean;
}

export const InfoStep: React.FC<InfoStepProps> = ({
    formData,
    tags,
    currentTag,
    applications,
    onInputChange,
    onAddTag,
    onRemoveTag,
    onCurrentTagChange,
    onNext,
    onBack,
    isSaving = false
}) => {
    const inputRef = useRef<any>(null);
    const latestOnAddTag = useRef(onAddTag);
    const latestOnRemoveTag = useRef(onRemoveTag);
    const latestOnCurrentTagChange = useRef(onCurrentTagChange);
    const latestOnNext = useRef(onNext);
    const latestOnBack = useRef(onBack);

    useEffect(() => { latestOnAddTag.current = onAddTag; }, [onAddTag]);
    useEffect(() => { latestOnRemoveTag.current = onRemoveTag; }, [onRemoveTag]);
    useEffect(() => { latestOnCurrentTagChange.current = onCurrentTagChange; }, [onCurrentTagChange]);
    useEffect(() => { latestOnNext.current = onNext; }, [onNext]);
    useEffect(() => { latestOnBack.current = onBack; }, [onBack]);

    const isValid =
        formData.name.trim() &&
        formData.description.trim() &&
        formData.category.trim() &&
        (formData.applicationId ? formData.applicationId.trim() : '');

    const handleAddClick = () => {
        const val = inputRef.current?.value || currentTag;
        latestOnAddTag.current(val);
    };

    const [splitRatio, setSplitRatio] = useState(0.65);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        const startX = e.clientX;
        const initialRatio = splitRatio;

        const handleMouseMove = (mmE: MouseEvent) => {
            const deltaX = mmE.clientX - startX;
            const deltaRatio = deltaX / window.innerWidth;
            const newRatio = Math.max(0.3, Math.min(0.8, initialRatio + deltaRatio));
            setSplitRatio(newRatio);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [splitRatio]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Top Toolbar */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: 'var(--mds-color-neutral-weakest-background)',
                borderRadius: '8px',
                border: '1px solid var(--mds-color-neutral-weak-decoration)'
            }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {onBack && (
                        <McButton
                            appearance="secondary"
                            onClick={() => latestOnBack.current?.()}
                        >
                            <McIcon icon="arrow-left" slot="prefix" />
                            Back
                        </McButton>
                    )}
                    <McButton
                        appearance="primary"
                        variant="filled"
                        onClick={() => latestOnNext.current()}
                        disabled={!isValid || isSaving}
                        style={{ backgroundColor: 'var(--mds-color-interactive-main-background, #ff6600)', borderColor: 'var(--mds-color-interactive-main-background, #ff6600)' }}
                    >
                        {isSaving ? <McLoadingIndicator slot="prefix" fit="small" /> : <McIcon icon="floppy-disk" slot="prefix" />}
                        {onBack ? (isSaving ? 'Updating...' : 'Update Query') : 'Next: SQL Content'}
                    </McButton>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row', gap: 0, alignItems: 'stretch' }}>
                {/* Left side: Query Info */}
                <div style={{ flex: splitRatio, minWidth: '300px' }}>
                    <McCard style={{ height: '100%' }}>
                        <div style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                                <McIcon icon="file" style={{ color: 'var(--mds-color-primary-main-decoration)', fontSize: '14px' }} />
                                <h3 className="mds-text--small-bold" style={{ margin: 0 }}>Metadata</h3>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <McInput
                                    label="Query ID"
                                    value={formData.queryId}
                                    disabled
                                    hint="Immutable unique identifier"
                                />

                                <McInput
                                    label="Query Name *"
                                    value={formData.name}
                                    input={(e: any) => onInputChange('name', e.target.value)}
                                    required
                                    placeholder="Enter a descriptive name"
                                />

                                <div className="mds-form-field">
                                    <McTextarea
                                        label="Description *"
                                        rows={3}
                                        value={formData.description}
                                        input={(e: any) => onInputChange('description', e.target.value)}
                                        placeholder="What does this query do?"
                                        required
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <McSelect
                                        label="Category *"
                                        value={formData.category}
                                        required
                                        data={getCategoryOptions()}
                                        optionselected={(e: any) => onInputChange('category', e.detail.value)}
                                    />

                                    <McSelect
                                        label="Application *"
                                        value={formData.applicationId}
                                        required
                                        data={applications.map((app: any) => ({ label: app.name || app.applicationId, value: app.applicationId }))}
                                        optionselected={(e: any) => onInputChange('applicationId', e.detail.value)}
                                        disabled={applications.length === 0}
                                        placeholder={applications.length === 0 ? "No apps" : "Select app"}
                                    />
                                </div>

                                <div className="mds-form-field">
                                    <McTextarea
                                        label="Notes (Optional)"
                                        rows={2}
                                        value={formData.notes}
                                        input={(e: any) => onInputChange('notes', e.target.value)}
                                        placeholder="Internal notes or documentation"
                                    />
                                </div>

                                <div style={{ 
                                    marginTop: '8px', 
                                    padding: '16px', 
                                    backgroundColor: 'var(--mds-color-neutral-weakest-background)', 
                                    borderRadius: '8px', 
                                    border: '1px dotted var(--mds-color-neutral-weak-decoration)' 
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                        <McIcon icon="lightning" style={{ color: 'var(--mds-color-warning-main-background, #f9a825)', fontSize: '14px' }} />
                                        <h4 className="mds-text--x-small-bold" style={{ margin: 0 }}>Version Activation</h4>
                                    </div>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input 
                                            type="checkbox" 
                                            checked={formData.makeCurrent} 
                                            onChange={(e) => onInputChange('makeCurrent', e.target.checked)} 
                                            style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        <span className="mds-text--small-bold">Make this version current (Active in Production)</span>
                                    </label>
                                    {!formData.makeCurrent && (
                                        <p className="mds-text--xx-small-regular" style={{ color: 'var(--mds-color-error-main-background)', marginTop: '4px', marginLeft: '24px' }}>
                                            Warning: Saving as draft will not update the production version.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </McCard>
                </div>

                {/* Resizer */}
                <div
                    onMouseDown={handleMouseDown}
                    style={{
                        width: '8px',
                        cursor: 'col-resize',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex: 10,
                    }}
                >
                    <div style={{ width: '1px', height: '30px', backgroundColor: 'var(--mds-color-neutral-weak-decoration)', borderRadius: '1px' }} />
                </div>

                {/* Right side: Tags */}
                <div style={{ flex: 1 - splitRatio, minWidth: '300px' }}>
                    <McCard style={{ height: '100%' }}>
                        <div style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                                <McIcon icon="tag" style={{ color: 'var(--mds-color-primary-main-decoration)', fontSize: '14px' }} />
                                <h3 className="mds-text--small-bold" style={{ margin: 0 }}>Classification & Tags</h3>
                            </div>

                            <p className="mds-text--x-small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', marginBottom: '12px' }}>
                                Use tags to organize and search for queries across components.
                            </p>

                            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap', minHeight: '36px', padding: '8px', borderRadius: '4px', backgroundColor: 'var(--mds-color-neutral-lightest-background)', border: '1px solid var(--mds-color-neutral-weak-decoration)' }}>
                                {tags.map((tag, index) => (
                                    <McTag
                                        key={index}
                                        label={tag}
                                        appearance="neutral-default"
                                        onClick={() => latestOnRemoveTag.current(tag)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <McIcon icon="times" slot="suffix" />
                                    </McTag>
                                ))}
                                {tags.length === 0 && <p className="mds-text--x-small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', fontStyle: 'italic' }}>No tags assigned</p>}
                            </div>

                            <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                                <div style={{ flex: 1 }}>
                                    <McInput
                                        ref={inputRef}
                                        label="New tag..."
                                        value={currentTag}
                                        input={(e: any) => latestOnCurrentTagChange.current(e.target.value)}
                                        onKeyDown={(e: any) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddClick();
                                            }
                                        }}
                                    />
                                </div>
                                <McButton
                                    appearance="secondary"
                                    onClick={handleAddClick}
                                    disabled={!currentTag.trim()}
                                >
                                    <McIcon icon="plus" slot="prefix" />
                                    Add
                                </McButton>
                            </div>
                        </div>
                    </McCard>
                </div>
            </div>
        </div>
    );
};
